/**
 * Redirect URL Utility
 * 
 * Handles redirect URL management for authentication flows.
 * Preserves full URLs (pathname + search params + hash) when redirecting
 * unauthenticated users to login, and restores them after successful authentication.
 */

import { Location } from 'react-router-dom';

// Session storage key for persisting redirect URL across auth flows
const REDIRECT_URL_KEY = 'auth_redirect_url';

// Routes that should never be redirect targets after login
const EXCLUDED_REDIRECT_ROUTES = [
  '/auth/logout',
  '/logout',
];

/**
 * Build full URL string from a React Router location object.
 * Combines pathname, search params, and hash.
 */
export function buildFullUrl(location: Location): string {
  const { pathname, search, hash } = location;
  return `${pathname}${search || ''}${hash || ''}`;
}

/**
 * Validate that a redirect URL is safe (internal only).
 * Rejects external URLs to prevent open redirect vulnerabilities.
 */
export function isValidRedirectUrl(url: string): boolean {
  if (!url) return false;
  
  // Reject external URLs (http://, https://, or protocol-relative //)
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    return false;
  }
  
  // Reject javascript: and data: URLs
  if (url.startsWith('javascript:') || url.startsWith('data:')) {
    return false;
  }
  
  // Check against excluded routes
  const pathname = url.split('?')[0].split('#')[0];
  if (EXCLUDED_REDIRECT_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'))) {
    return false;
  }
  
  // Must be a relative URL starting with /
  return url.startsWith('/');
}

/**
 * Get the default redirect URL based on user role.
 */
export function getDefaultRedirect(userRole?: string): string {
  switch (userRole) {
    case 'tenant':
      return '/tenant';
    case 'super_admin':
    case 'admin':
      return '/dashboard';
    default:
      return '/dashboard';
  }
}

/**
 * Get the validated redirect URL from location state.
 * Falls back to role-based default if URL is invalid.
 * 
 * @param locationState - The location.state object from React Router
 * @param userRole - The authenticated user's role for default fallback
 */
export function getRedirectUrl(locationState: any, userRole?: string): string {
  const from = locationState?.from;
  
  // If 'from' is a string (new format), use it directly
  if (typeof from === 'string' && isValidRedirectUrl(from)) {
    return from;
  }
  
  // Legacy support: if 'from' is a location object, build the URL
  if (from && typeof from === 'object' && from.pathname) {
    const fullUrl = `${from.pathname}${from.search || ''}${from.hash || ''}`;
    if (isValidRedirectUrl(fullUrl)) {
      return fullUrl;
    }
  }
  
  // Fallback to role-based default
  return getDefaultRedirect(userRole);
}

/**
 * Resolve explicit auth intent from query params (e.g. catalogue -> login -> explore properties).
 * Returns null when no supported intent exists.
 */
export function getIntentRedirectFromSearch(search: string, userRole?: string): string | null {
  if (!search) return null;

  const params = new URLSearchParams(search);
  const redirectIntent = params.get('redirect');

  if (redirectIntent !== 'booking') return null;

  // Booking intent flow is tenant-only.
  if (userRole && userRole !== 'tenant') {
    return getDefaultRedirect(userRole);
  }

  const unitId = params.get('unitId');
  const propertyId = params.get('propertyId');
  const companyId = params.get('companyId');
  const source = params.get('source') || 'catalogue';
  const returnUrl = params.get('returnUrl');

  const targetParams = new URLSearchParams();
  if (unitId) targetParams.set('unitId', unitId);
  if (propertyId) targetParams.set('propertyId', propertyId);
  if (companyId) targetParams.set('companyId', companyId);
  targetParams.set('source', source);
  if (returnUrl) targetParams.set('returnUrl', returnUrl);

  const query = targetParams.toString();
  return query ? `/tenant/properties?${query}` : '/tenant/properties';
}

/**
 * Store redirect URL in session storage.
 * Used to persist the URL across login/register navigation.
 */
export function storeRedirectUrl(url: string): void {
  if (isValidRedirectUrl(url)) {
    console.log('[RedirectUrl] Storing redirect URL:', url);
    sessionStorage.setItem(REDIRECT_URL_KEY, url);
  } else {
    console.log('[RedirectUrl] URL validation failed, not storing:', url);
  }
}

/**
 * Retrieve redirect URL from session storage (does NOT clear it).
 * Call clearRedirectUrl() separately after successful navigation.
 */
export function retrieveRedirectUrl(): string | null {
  const url = sessionStorage.getItem(REDIRECT_URL_KEY);
//   console.log('[RedirectUrl] Retrieved from session storage:', url);
  
  if (url && isValidRedirectUrl(url)) {
    return url;
  }
  return null;
}

/**
 * Clear stored redirect URL without retrieving.
 */
export function clearRedirectUrl(): void {
  sessionStorage.removeItem(REDIRECT_URL_KEY);
}
